import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  List,
  Modal,
  Select,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { categoriesApi, dashboardApi, operationRequestsApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import type { OperationRequest, RequestStatus, UpdateOperationRequestBody } from '../api/types';
import { AmountInput, positiveAmountRule } from '../components/AmountInput';
import { ErrorBlock, LoadingBlock } from '../components/PageStates';
import { useStoreContext } from '../context/StoreContext';
import { balanceColor } from '../lib/balanceColors';
import { useJustSubmittedId } from '../lib/justSubmitted';
import { formatKopeks, rubToKopeks } from '../lib/money';
import { monthFromSearch } from '../lib/returnTo';
import { summarizePending } from '../lib/pendingSummary';
import { StoreDayGridTable } from './daygrid/StoreDayGridTable';

const STATUS_COLOR: Record<RequestStatus, string> = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
};

interface EditValues {
  amount: string;
  date: Dayjs;
  categoryId?: string;
  comment?: string;
}

/**
 * The store SELLER's home — their store, and nothing else (SPEC §1). Shows the
 * store balance and the seller's own submissions with their approval status.
 * Money entry is the FAB (SellerEntryDrawer); this page manages what was sent.
 */
export function SellerHomePage(): JSX.Element {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { currentStore } = useStoreContext();
  const location = useLocation();
  const [editing, setEditing] = useState<OperationRequest | null>(null);
  // `?month=` brings the seller back to the month they opened a накладная from.
  const [month, setMonth] = useState<Dayjs>(() => monthFromSearch(location.search));
  const justSubmittedId = useJustSubmittedId();
  const monthStr = month.format('YYYY-MM');
  const [form] = Form.useForm<EditValues>();

  const dashboardQuery = useQuery({
    queryKey: queryKeys.dashboard({ storeId: currentStore?.id ?? null }),
    queryFn: () => dashboardApi.view({ storeId: currentStore?.id ?? null }),
  });
  const requestsQuery = useQuery({
    queryKey: queryKeys.operationRequests({ mine: true }),
    queryFn: () => operationRequestsApi.listMine(),
  });
  const categoriesQuery = useQuery({
    queryKey: queryKeys.categories(false),
    queryFn: () => categoriesApi.list(false),
    enabled: editing?.type === 'EXPENSE',
  });

  const invalidate = (): void => invalidateMoneyData(queryClient);

  const withdrawMutation = useMutation({
    mutationFn: (id: string) => operationRequestsApi.remove(id),
    onSuccess: () => {
      message.success(t('seller.withdrawn'));
      invalidate();
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const editMutation = useMutation({
    mutationFn: (values: EditValues) => {
      const body: UpdateOperationRequestBody = {
        amount: rubToKopeks(values.amount),
        date: values.date.format('YYYY-MM-DD'),
        ...(editing?.type === 'EXPENSE' && values.categoryId
          ? { categoryId: values.categoryId }
          : {}),
        comment: values.comment?.trim() ?? '',
      };
      return operationRequestsApi.update(editing!.id, body);
    },
    onSuccess: () => {
      message.success(t('common.saved'));
      invalidate();
      setEditing(null);
    },
    onError: (error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const openEdit = (req: OperationRequest): void => {
    setEditing(req);
    form.setFieldsValue({
      amount: (req.amount / 100).toString(),
      date: dayjs(req.date),
      categoryId: req.categoryId ?? undefined,
      comment: req.comment ?? '',
    });
  };

  const confirmWithdraw = (req: OperationRequest): void => {
    modal.confirm({
      title: t('seller.withdrawConfirm'),
      okText: t('seller.withdraw'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: () => withdrawMutation.mutateAsync(req.id).then(() => undefined),
    });
  };

  const kindLabel = (req: OperationRequest): string => {
    if (req.type === 'STORE_PAYMENT') {
      return req.paymentMethod ? t(`payMethod.${req.paymentMethod}`) : t('opType.STORE_PAYMENT');
    }
    return req.categoryName ?? t('opType.EXPENSE');
  };

  const balance = dashboardQuery.data?.totalStoreBalance ?? null;
  const pending = summarizePending(requestsQuery.data ?? []);
  const history = (requestsQuery.data ?? []).filter((r) => r.status !== 'PENDING');

  const renderRequest = (req: OperationRequest): JSX.Element => (
    <List.Item
      style={{
        paddingInline: 4,
        alignItems: 'flex-start',
        // Point at the row the seller just created, so the submit visibly landed.
        ...(req.id === justSubmittedId
          ? { background: '#fffbe6', boxShadow: 'inset 3px 0 0 #faad14' }
          : {}),
      }}
      extra={
        req.status === 'PENDING' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Button size="small" onClick={() => openEdit(req)}>
              {t('common.edit')}
            </Button>
            <Button size="small" danger onClick={() => confirmWithdraw(req)}>
              {t('seller.withdraw')}
            </Button>
          </div>
        ) : null
      }
    >
      <List.Item.Meta
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Typography.Text strong>{formatKopeks(req.amount)}</Typography.Text>
            <Tag color={STATUS_COLOR[req.status]}>{t(`seller.status.${req.status}`)}</Tag>
          </span>
        }
        description={
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {dayjs(req.date).format('DD.MM.YYYY')} · {kindLabel(req)}
            {req.status === 'PENDING' ? ` · ${dayjs(req.createdAt).format('HH:mm')}` : ''}
            {req.comment ? ` · ${req.comment}` : ''}
            {req.status === 'REJECTED' && req.reviewNote ? (
              <span style={{ display: 'block', color: '#cf1322' }}>
                {t('seller.rejectNote')}: {req.reviewNote}
              </span>
            ) : null}
          </Typography.Text>
        }
      />
    </List.Item>
  );

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card size="small">
        <Typography.Text type="secondary">
          {currentStore?.name ?? t('seller.myStore')}
        </Typography.Text>
        <div>
          {balance !== null ? (
            <Typography.Text strong style={{ fontSize: 22, color: balanceColor('store', balance) }}>
              {formatKopeks(balance)}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary">…</Typography.Text>
          )}
        </div>
      </Card>

      {/* The store jadval (day-grid), read-only — approved money only; entered
          items appear here once an admin confirms them. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button icon={<LeftOutlined />} onClick={() => setMonth((m) => m.subtract(1, 'month'))} />
        <DatePicker
          picker="month"
          value={month}
          onChange={(m) => m && setMonth(m.startOf('month'))}
          allowClear={false}
          format="MMMM YYYY"
          style={{ flex: 1 }}
          inputReadOnly
        />
        <Button icon={<RightOutlined />} onClick={() => setMonth((m) => m.add(1, 'month'))} />
      </div>
      {currentStore ? (
        <Card size="small" styles={{ body: { padding: 0 } }}>
          <StoreDayGridTable id={currentStore.id} month={monthStr} readOnly />
        </Card>
      ) : null}

      {requestsQuery.isLoading ? (
        <LoadingBlock />
      ) : requestsQuery.isError || !requestsQuery.data ? (
        <ErrorBlock onRetry={() => void requestsQuery.refetch()} />
      ) : (
        <>
          {/* Waiting entries FIRST and on their own. A pending entry moves no
              balance and never shows in the jadval above, so without this card
              the seller has no evidence it landed — and types it again. */}
          {pending.count > 0 ? (
            <>
              <Typography.Title level={5} style={{ margin: '4px 0 0' }}>
                {t('seller.pendingSummary', {
                  n: pending.count,
                  sum: formatKopeks(pending.total),
                })}
              </Typography.Title>
              <Card
                size="small"
                styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}
                style={{ borderColor: '#faad14' }}
              >
                <List dataSource={pending.requests} renderItem={renderRequest} />
              </Card>
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {t('seller.pendingHint')}
              </Typography.Text>
            </>
          ) : null}

          <Typography.Title level={5} style={{ margin: '4px 0 0' }}>
            {t('seller.history')}
          </Typography.Title>
          {history.length === 0 ? (
            <Typography.Text type="secondary">{t('seller.noEntries')}</Typography.Text>
          ) : (
            <Card size="small" styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
              <List dataSource={history} renderItem={renderRequest} />
            </Card>
          )}
        </>
      )}

      <Modal
        open={editing !== null}
        onCancel={() => setEditing(null)}
        title={t('seller.editEntry')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={editMutation.isPending}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form<EditValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(v) => editMutation.mutate(v)}
        >
          {editing?.type === 'EXPENSE' ? (
            <Form.Item
              name="categoryId"
              label={t('common.category')}
              rules={[{ required: true, message: t('common.categoryRequired') }]}
            >
              <Select
                size="large"
                options={(categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
                loading={categoriesQuery.isLoading}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="amount" label={t('common.amount')} rules={[positiveAmountRule(t)]}>
            <AmountInput />
          </Form.Item>
          <Form.Item
            name="date"
            label={t('common.date')}
            rules={[{ required: true, message: t('common.dateRequired') }]}
          >
            <DatePicker
              size="large"
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              allowClear={false}
              inputReadOnly
            />
          </Form.Item>
          <Form.Item name="comment" label={t('common.comment')}>
            <Input size="large" maxLength={1000} autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
